import React, { useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../../context/WalletContext';
import { useContracts } from '../../context/ContractContext';
import { useNotify } from '../../context/NotifyContext';
import Card from '../common/Card';
import Button from '../common/Button';
import Loader from '../common/Loader';
import InlineLoader from '../common/InlineLoader';
import { formatUSDT } from '../../utils/format';
import { parseError } from '../../utils/errors';

export default function PendingROI({ onSuccess }) {
  const { account, wrongNetwork } = useWallet();
  const { main, mainW, views } = useContracts();
  const { notify } = useNotify();
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(ethers.BigNumber.from(0));
  const [uid, setUid] = useState('0');
  const [tx, setTx] = useState(null);
  const mountedRef = useRef(true);

  const load = async () => {
    try {
      if (!account) { setLoading(false); return; }
      const id = await main.addrToId(account);
      if (id.eq(0)) { if (mountedRef.current) setLoading(false); return; }
      const p = await views.calcPendingROI(id).catch(() => ethers.BigNumber.from(0));
      if (!mountedRef.current) return;
      setUid(id.toString());
      setPending(p);
      setLoading(false);
    } catch (_) {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    load();
    return () => { mountedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, main, views]);

  const doUpdate = async () => {
    if (!account || uid === '0') return;
    if (wrongNetwork) { notify('error', 'Switch network first'); return; }
    try {
      setTx('Syncing pending ROI');
      const t = await mainW.updateROI(uid);
      setTx('Waiting for confirmation');
      await t.wait();
      notify('success', 'ROI synced to your balance');
      setTx(null);
      setLoading(true);
      await load();
      if (onSuccess) onSuccess();
    } catch (err) {
      setTx(null);
      notify('error', parseError(err));
    }
  };

  if (loading) return <Card title="Pending ROI"><InlineLoader /></Card>;
  if (!account || uid === '0') return null;

  return (
    <>
      {tx && <Loader text={tx} />}
      <Card title="Pending ROI" glow>
        <div className="flex justify-between items-center gap-4 wrap">
          <div>
            <div className="text-muted" style={{ fontSize: 11, letterSpacing: 1.5 }}>UNCLAIMED ROI</div>
            <div className="mono text-success" style={{ fontSize: 26, fontWeight: 700 }}>
              ${formatUSDT(pending)}
            </div>
            <div className="text-dim" style={{ fontSize: 11 }}>
              Sync to move this into your withdrawable balance
            </div>
          </div>
          <Button variant="success" onClick={doUpdate} disabled={pending.eq(0)}>
            SYNC ROI
          </Button>
        </div>
      </Card>
    </>
  );
}
